import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { coffeeDispatches, clients as clientsApi, blendNames as blendNamesApi } from '../lib/api';
import { inr, rs } from '../lib/format';
import { Truck, Plus, Trash2, Download, Pencil, FileText, Sheet } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import * as XLSX from 'xlsx';
import { downloadPdfReport } from '../lib/pdfReport';
import { downloadInvoicePdf } from '../lib/invoicePdf';
import { DispatchFormModal } from './DispatchFormModal';
import { EditDispatchModal } from './EditDispatchModal';
import { LevelBadge, PayBadge } from './badges';

const PRESETS = [
  { k: 'all', label: 'All Time' },
  { k: 'today', label: 'Today' },
  { k: 'yesterday', label: 'Yesterday' },
  { k: 'last_7', label: 'Last 7 Days' },
  { k: 'this_month', label: 'This Month' },
  { k: 'last_month', label: 'Last Month' },
  { k: 'custom', label: 'Custom Range' },
];

function computeRange(preset, customFrom, customTo) {
  const now = new Date();
  switch (preset) {
    case 'today': return { from: startOfDay(now), to: endOfDay(now), label: 'Today' };
    case 'yesterday': { const y = subDays(now, 1); return { from: startOfDay(y), to: endOfDay(y), label: 'Yesterday' }; }
    case 'last_7': return { from: startOfDay(subDays(now, 6)), to: endOfDay(now), label: 'Last 7 Days' };
    case 'this_month': return { from: startOfMonth(now), to: endOfMonth(now), label: 'This Month' };
    case 'last_month': { const lm = subMonths(now, 1); return { from: startOfMonth(lm), to: endOfMonth(lm), label: 'Last Month' }; }
    case 'custom':
      if (!customFrom || !customTo) return { from: null, to: null, label: 'Custom' };
      return { from: startOfDay(new Date(customFrom)), to: endOfDay(new Date(customTo)), label: `${customFrom} to ${customTo}` };
    default: return { from: null, to: null, label: 'All Time' };
  }
}

const buildExcelRows = (dispatches) => {
  const rows = [];
  dispatches.forEach((d) => {
    (d.items || []).forEach((it, i) => {
      const comps = it.components?.length ? it.components : [null];
      comps.forEach((comp, j) => {
        rows.push({
          Date: j === 0 ? format(new Date(d.dispatch_date), 'yyyy-MM-dd') : '',
          Invoice: j === 0 ? (d.invoice_no || '') : '',
          Client: j === 0 ? (d.client_name || '') : '',
          Blend: j === 0 ? (it.blend_name || '') : '',
          Coffee: comp ? comp.category_name : it.category_name,
          Roast: comp ? comp.roast_level : it.roast_level,
          'Qty (kg)': comp ? comp.quantity_kg : it.quantity_kg,
          'Price/kg': j === 0 ? it.sale_price_per_kg : '',
          'Line Total': j === 0 ? +(it.quantity_kg * (it.sale_price_per_kg || 0)).toFixed(2) : '',
          'Cost/kg': comp ? comp.cost_per_kg : it.cost_per_kg,
          'Line Cost': comp
            ? +(comp.quantity_kg * (comp.cost_per_kg || 0)).toFixed(2)
            : +(it.quantity_kg * (it.cost_per_kg || 0)).toFixed(2),
          Payment: i === 0 && j === 0 ? (d.payment_status || 'Unpaid') : '',
          'Amount Paid': i === 0 && j === 0 ? (d.amount_paid || 0) : '',
          By: i === 0 && j === 0 ? (d.dispatched_by || '') : '',
        });
      });
    });
  });
  return rows;
};

const DispatchRow = ({ d, onEdit, onDelete }) => {
  const profit = (d.total_sale_value || 0) - (d.total_cost_value || 0);
  return (
    <tr className="border-t hover:bg-gray-50 align-top" data-testid={`dispatch-row-${d.id}`}>
      <td className="py-3 px-4 text-sm whitespace-nowrap">{format(new Date(d.dispatch_date), 'MMM dd, yyyy')}</td>
      <td className="py-3 px-4 text-sm font-medium text-gray-700">{d.invoice_no || '-'}</td>
      <td className="py-3 px-4 text-sm font-semibold text-gray-800">{d.client_name || '-'}</td>
      <td className="py-3 px-4">
        <div className="space-y-1.5">
          {(d.items || []).map((it, i) => (
            <div key={`${it.blend_name || it.category_id}-${it.roast_level}-${i}`} className="text-sm">
              {it.components?.length ? (
                <>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-gray-800">{it.blend_name || 'Blend'}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">BLEND</span>
                    <span className="text-gray-500 text-xs">
                      {it.quantity_kg}kg{it.sale_price_per_kg > 0 ? ` @ ${inr(it.sale_price_per_kg)}/kg` : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {it.components.map((c) => `${c.category_name} ${c.roast_level} ${c.quantity_kg}kg`).join(' + ')}
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-gray-800">{it.category_name}</span>
                  <LevelBadge level={it.roast_level} />
                  <span className="text-gray-500 text-xs">
                    {it.quantity_kg}kg{it.sale_price_per_kg > 0 ? ` @ ${inr(it.sale_price_per_kg)}/kg` : ''}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </td>
      <td className="py-3 px-4 text-right font-bold text-blue-600">{(d.total_quantity_kg || 0).toFixed(2)}</td>
      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-800">
        {d.total_sale_value > 0 ? inr(d.total_sale_value) : '-'}
      </td>
      <td className="py-3 px-4 text-right text-sm text-gray-600">
        {d.total_cost_value > 0 ? inr(d.total_cost_value) : '-'}
      </td>
      <td className={`py-3 px-4 text-right text-sm font-semibold ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
        {d.total_sale_value > 0 ? inr(profit) : '-'}
      </td>
      <td className="py-3 px-4 text-center">
        <PayBadge status={d.payment_status} />
        {d.payment_status === 'Partial' && (
          <p className="text-[10px] text-gray-500 mt-0.5">{inr(d.amount_paid)} paid</p>
        )}
      </td>
      <td className="py-3 px-4 text-center whitespace-nowrap">
        <button onClick={() => downloadInvoicePdf(d)} data-testid={`invoice-dispatch-${d.id}`}
          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Download invoice PDF">
          <FileText className="w-4 h-4" />
        </button>
        <button onClick={onEdit} data-testid={`edit-dispatch-${d.id}`}
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit client / invoice / prices">
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={onDelete} data-testid={`delete-dispatch-${d.id}`}
          className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

export const DispatchedTab = ({ stock, cats, onStockChanged }) => {
  const [dispatches, setDispatches] = useState([]);
  const [costs, setCosts] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [blendNameList, setBlendNameList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [d, c, cl, bn] = await Promise.all([
        coffeeDispatches.getAll(), coffeeDispatches.getCosts(), clientsApi.getAll(),
        blendNamesApi.getAll(),
      ]);
      setDispatches(d.data);
      setCosts(c.data);
      setClientList(cl.data);
      setBlendNameList(bn.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const range = useMemo(() => computeRange(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const filtered = useMemo(() => {
    if (!range.from || !range.to) return dispatches;
    return dispatches.filter((d) => {
      const dt = new Date(d.dispatch_date);
      return dt >= range.from && dt <= range.to;
    });
  }, [dispatches, range]);

  const totals = useMemo(() => ({
    kg: filtered.reduce((s, d) => s + (d.total_quantity_kg || 0), 0),
    sale: filtered.reduce((s, d) => s + (d.total_sale_value || 0), 0),
    cost: filtered.reduce((s, d) => s + (d.total_cost_value || 0), 0),
    outstanding: filtered.reduce((s, d) => s + ((d.total_sale_value || 0) - (d.amount_paid || 0)), 0),
  }), [filtered]);

  const clientNames = clientList.map((c) => c.name);

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete this dispatch of ${(d.total_quantity_kg || 0).toFixed(2)}kg? Stock will be returned.`)) return;
    try {
      await coffeeDispatches.delete(d.id);
      fetchData();
      onStockChanged();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(buildExcelRows(filtered));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dispatches');
    XLSX.writeFile(wb, `dispatches_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const handleDownloadPdf = () => {
    downloadPdfReport({
      title: 'Dispatch Report',
      subtitle: range.label,
      summary: [
        { label: 'Dispatches', value: filtered.length },
        { label: 'Total Quantity', value: `${totals.kg.toFixed(2)} kg` },
        { label: 'Total Sales', value: rs(totals.sale) },
        { label: 'Est. Cost', value: rs(totals.cost) },
        { label: 'Gross Profit', value: rs(totals.sale - totals.cost) },
        { label: 'Outstanding', value: rs(totals.outstanding) },
      ],
      tables: [
        {
          title: 'Dispatch Records',
          head: ['Date', 'Invoice', 'Client', 'Items', 'Qty (kg)', 'Sale', 'Cost', 'Profit', 'Payment'],
          rows: filtered.map((d) => [
            format(new Date(d.dispatch_date), 'yyyy-MM-dd'),
            d.invoice_no || '-',
            d.client_name || '-',
            (d.items || []).map((it) =>
              it.components?.length
                ? `${it.blend_name || 'Blend'} ${it.quantity_kg}kg (${it.components.map((c) => `${c.category_name} ${c.roast_level} ${c.quantity_kg}kg`).join(' + ')})`
                : `${it.category_name} ${it.roast_level} ${it.quantity_kg}kg`
            ).join('\n'),
            (d.total_quantity_kg || 0).toFixed(2),
            rs(d.total_sale_value),
            rs(d.total_cost_value),
            rs((d.total_sale_value || 0) - (d.total_cost_value || 0)),
            d.payment_status || 'Unpaid',
          ]),
        },
      ],
      filename: 'dispatch-report.pdf',
    });
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPreset(p.k)} data-testid={`dispatch-preset-${p.k}`}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${preset === p.k ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={handleDownloadExcel} data-testid="download-dispatch-excel"
            className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-emerald-600 hover:to-green-700 transition shadow">
            <Sheet className="w-4 h-4" /><span>Excel</span>
          </button>
          <button onClick={handleDownloadPdf} data-testid="download-dispatch-pdf"
            className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow">
            <Download className="w-4 h-4" /><span>PDF</span>
          </button>
          <button onClick={() => setShowForm(true)} data-testid="new-dispatch-button"
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition shadow">
            <Plus className="w-4 h-4" /><span>Log Dispatch</span>
          </button>
        </div>
      </div>

      {preset === 'custom' && (
        <div className="flex gap-3">
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 border rounded-lg" />
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 border rounded-lg" />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Dispatches</p>
          <p className="text-2xl font-bold text-gray-800 mt-1" data-testid="dispatch-stat-count">{filtered.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Total Quantity</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{totals.kg.toFixed(2)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Total Sales</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="dispatch-stat-sales">{inr(totals.sale)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Gross Profit</p>
          <p className={`text-2xl font-bold mt-1 ${totals.sale - totals.cost >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="dispatch-stat-profit">
            {inr(totals.sale - totals.cost)}
          </p>
          {totals.sale > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">Margin {(((totals.sale - totals.cost) / totals.sale) * 100).toFixed(1)}%</p>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Outstanding</p>
          <p className={`text-2xl font-bold mt-1 ${totals.outstanding > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="dispatch-stat-outstanding">
            {inr(totals.outstanding)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No dispatches in this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Invoice</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Items</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Qty (kg)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Sale ₹</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Cost ₹</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Profit ₹</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Payment</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <DispatchRow key={d.id} d={d} onEdit={() => setEditing(d)} onDelete={() => handleDelete(d)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <DispatchFormModal
          cats={cats} stock={stock} costs={costs} clientNames={clientNames} blendNames={blendNameList}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchData(); onStockChanged(); }}
        />
      )}
      {editing && (
        <EditDispatchModal
          dispatch={editing} clientNames={clientNames}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); }}
        />
      )}
    </>
  );
};
